import React from 'react'

import { HiOutlineXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import BetaChip from '@/components/common/Chip/BetaChip'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

export type ChipVariant =
  | 'muted'
  | 'primary'
  | 'info'
  | 'positive'
  | 'negative'
  | 'danger'
  | 'blue'
  | 'blue-strong'
  | 'purple'
  | 'purple-strong'
  | 'yellow'
  | 'yellow-strong'
  | 'orange'
  | 'orange-strong'
  | 'red'
  | 'red-strong'
  | 'green'
  | 'green-strong'
  | 'emerald'
  | 'emerald-strong'

export interface ChipProps {
  content?: string | React.ReactNode
  className?: string
  children?: React.ReactNode
  truncate?: boolean
  variant?: ChipVariant
  tooltip?: React.ReactNode
  tooltipContentProps?: React.ComponentProps<typeof TooltipContent>
  onDelete?: () => void
}

export const getClassName = (variant: ChipVariant) => {
  switch (variant) {
    case 'muted':
      return 'bg-primary-100 dark:bg-primary-800 text-primary-900 dark:text-primary-100'
    case 'primary':
      return 'bg-accent-400 dark:bg-accent-500 text-white font-medium'
    case 'info':
      return 'text-primary-900 dark:text-primary-100 border border-blue-500 dark:border-blue-500 bg-blue-400/20 dark:bg-blue-600/20'
    case 'positive':
      return 'text-green-900 dark:text-green-200 border border-green-500 dark:border-green-500 bg-green-400/20 dark:bg-green-600/20'
    case 'negative':
      return 'text-red-900 dark:text-red-100 border border-red-500 dark:border-red-500 bg-red-400/20 dark:bg-red-600/20'
    case 'danger':
      return 'font-medium text-white bg-red-600 dark:bg-red-600 transition duration-100 border border-red-700 dark:border-red-700'
    case 'blue':
      return 'text-blue-900 dark:text-blue-200 border border-blue-500 dark:border-blue-500 bg-blue-400/20 dark:bg-blue-600/20'
    case 'blue-strong':
      return 'font-medium text-white bg-blue-600 dark:bg-blue-600/80 transition duration-100 border border-blue-700 dark:border-blue-700'
    case 'purple':
      return 'text-purple-900 dark:text-purple-200 border border-purple-500 dark:border-purple-500 bg-purple-400/20 dark:bg-purple-600/20'
    case 'purple-strong':
      return 'font-medium text-white bg-purple-600 dark:bg-purple-600/80 transition duration-100 border border-purple-700 dark:border-purple-700'
    case 'yellow':
      return 'text-yellow-900 dark:text-yellow-200 border border-yellow-500 dark:border-yellow-600 bg-yellow-200/80 dark:bg-yellow-600/20'
    case 'yellow-strong':
      return 'font-medium text-white bg-yellow-500 dark:bg-yellow-600/80 transition duration-100 border border-yellow-700 dark:border-yellow-700'
    case 'orange':
      return 'text-orange-900 dark:text-orange-200 border border-orange-500 dark:border-orange-600 bg-orange-400/20 dark:bg-orange-600/20'
    case 'orange-strong':
      return 'font-medium text-white bg-orange-600 dark:bg-orange-600/80 transition duration-100 border border-orange-700 dark:border-orange-700'
    case 'red':
      return 'text-red-900 dark:text-red-100 border border-red-500 dark:border-red-600 bg-red-400/20 dark:bg-red-600/20'
    case 'red-strong':
      return 'font-medium text-white bg-red-600 dark:bg-red-600/80 transition duration-100 border border-red-700 dark:border-red-700'
    case 'green':
      return 'text-green-900 dark:text-green-100 border border-green-500 dark:border-green-600 bg-green-400/20 dark:bg-green-600/20'
    case 'green-strong':
      return 'font-medium text-white bg-green-600 dark:bg-green-700 transition duration-100 border border-green-700 dark:border-green-700'
    case 'emerald':
      return 'text-emerald-900 dark:text-emerald-100 border border-emerald-500 dark:border-emerald-600 bg-emerald-400/20 dark:bg-emerald-600/20'
    case 'emerald-strong':
      return 'font-medium text-white bg-emerald-600 dark:bg-emerald-600/80 transition duration-100 border border-emerald-700 dark:border-emerald-700'
  }
}

export const LoadingChip: React.FC = () => {
  return (
    <div className="h-2 w-12 bg-primary-300 dark:bg-primary-600 rounded-md animate-pulse" />
  )
}

const Chip: React.FC<ChipProps> = ({
  content,
  children,
  className = '',
  truncate = true,
  tooltip = null,
  tooltipContentProps,
  variant = 'muted',
  onDelete = null,
}) => {
  return (
    <div
      className={twMerge(
        'flex items-center justify-center w-fit max-w-full rounded-md gap-x-1 pr-1 text-sm',
        getClassName(variant),
        onDelete ? 'pl-[7px]' : 'pl-1',
        className,
      )}
    >
      <Tooltip>
        <TooltipTrigger
          className={twMerge('cursor-default', truncate ? 'truncate' : '')}
        >
          <span className={twMerge('-mb-[0.25px]')}>{content || children}</span>
        </TooltipTrigger>
        {tooltip && (
          <TooltipContent {...tooltipContentProps}>{tooltip}</TooltipContent>
        )}
      </Tooltip>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={
            typeof content === 'string' ? `Remove ${content}` : 'Remove'
          }
          // after:* widens the 16px ✕ to a ~32px hit area without changing the
          // chip's visual size; relative anchors the pseudo-element + focus ring.
          className="relative rounded-sm transition duration-150 hover:opacity-60 after:absolute after:left-1/2 after:top-1/2 after:h-8 after:w-8 after:-translate-x-1/2 after:-translate-y-1/2 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
        >
          <HiOutlineXMark className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

export { BetaChip }

export default Chip
